import React, { useState } from 'react';
import { submitContactForm } from '../utils/api';

const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    service: '',
    message: '',
  });
  const [status, setStatus] = useState({ type: '', message: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setStatus({ type: '', message: '' });

    try {
      const response = await submitContactForm(formData);
      setStatus({
        type: 'success',
        message: response.message || 'Thank you! We will contact you soon.',
      });
      setFormData({
        name: '',
        email: '',
        phone: '',
        company: '',
        service: '',
        message: '',
      });
    } catch (error) {
      setStatus({
        type: 'error',
        message: error.message || 'Something went wrong. Please try again.',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="page">
      {/* Hero Section */}
      <section className="hero" style={{minHeight: '50vh', paddingTop: 'calc(80px + var(--spacing-xl))'}}>
        <div className="container hero-content">
          <span className="section-label">Get In Touch</span>
          <h1>Contact Us</h1>
          <p>Ready to start your project? We'd love to hear from you. Let's discuss how we can help transform your business.</p>
        </div>
      </section>

      {/* Contact Section */}
      <section className="section">
        <div className="container">
          <div className="grid" style={{gridTemplateColumns: '1.5fr 1fr', gap: 'var(--spacing-xl)', alignItems: 'start'}}>
            {/* Contact Form */}
            <div className="reveal-left">
              <div className="card" style={{padding: 'var(--spacing-xl)'}}>
                <h2 style={{marginBottom: 'var(--spacing-md)'}}>Send Us a Message</h2>
                <p style={{color: 'var(--color-gray-dark)', marginBottom: 'var(--spacing-lg)'}}>Fill out the form below and we'll get back to you within 24 hours.</p>
                
                {status.message && (
                  <div style={{
                    padding: 'var(--spacing-md)',
                    marginBottom: 'var(--spacing-md)',
                    borderRadius: '8px',
                    background: status.type === 'success' ? 'rgba(26, 95, 138, 0.2)' : 'rgba(220, 53, 69, 0.2)',
                    border: `1px solid ${status.type === 'success' ? 'var(--color-blue-light)' : '#dc3545'}`
                  }}>
                    {status.message}
                  </div>
                )}

                <form onSubmit={handleSubmit}>
                  <div className="form-group">
                    <label htmlFor="name">Full Name *</label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      className="form-control"
                      placeholder="John Doe"
                      value={formData.name}
                      onChange={handleChange}
                      required
                    />
                  </div>
                  
                  <div className="form-group">
                    <label htmlFor="email">Email Address *</label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      className="form-control"
                      placeholder="john@company.com"
                      value={formData.email}
                      onChange={handleChange}
                      required
                    />
                  </div>
                  
                  <div className="form-group">
                    <label htmlFor="phone">Phone Number</label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      className="form-control"
                      placeholder="+94 77 23 53 975"
                      value={formData.phone}
                      onChange={handleChange}
                    />
                  </div>
                  
                  <div className="form-group">
                    <label htmlFor="company">Company Name</label>
                    <input
                      type="text"
                      id="company"
                      name="company"
                      className="form-control"
                      placeholder="Your Company"
                      value={formData.company}
                      onChange={handleChange}
                    />
                  </div>
                  
                  <div className="form-group">
                    <label htmlFor="service">Interested In *</label>
                    <select
                      id="service"
                      name="service"
                      className="form-control"
                      value={formData.service}
                      onChange={handleChange}
                      required
                    >
                      <option value="">Select a service</option>
                      <option value="custom-software">Custom Software Development</option>
                      <option value="web-development">Website & Web App Development</option>
                      <option value="data-analytics">Data Analytics & BI</option>
                      <option value="sharepoint">SharePoint Solutions</option>
                      <option value="nok-spa">Nok Spa Management Suite</option>
                      <option value="sevenhc-pos">SEVENHC POS</option>
                      <option value="consultation">General Consultation</option>
                      <option value="other">Other</option>
                    </select>
                  </div>
                  
                  <div className="form-group">
                    <label htmlFor="message">Message *</label>
                    <textarea
                      id="message"
                      name="message"
                      className="form-control"
                      placeholder="Tell us about your project..."
                      value={formData.message}
                      onChange={handleChange}
                      required
                    ></textarea>
                  </div>
                  
                  <button 
                    type="submit" 
                    className="btn btn-primary" 
                    style={{width: '100%', padding: '1rem', fontSize: '1.125rem'}}
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? 'Sending...' : 'Send Message'}
                  </button>
                </form>
              </div>
            </div>

            {/* Contact Information */}
            <div className="reveal-right">
              <div style={{display: 'flex', flexDirection: 'column', gap: 'var(--spacing-md)'}}>
                {/* Contact Info Card */}
                <div className="card" style={{padding: 'var(--spacing-lg)'}}>
                  <h3 style={{marginBottom: 'var(--spacing-md)'}}>Contact Information</h3>
                  
                  <div style={{display: 'flex', flexDirection: 'column', gap: 'var(--spacing-md)'}}>
                    <div style={{display: 'flex', alignItems: 'start', gap: 'var(--spacing-sm)'}}>
                      <div style={{minWidth: '40px', height: '40px', background: 'linear-gradient(135deg, var(--color-blue-dark), var(--color-blue-light))', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center'}}>
                        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"></path>
                        </svg>
                      </div>
                      <div>
                        <h4 style={{marginBottom: '0.25rem', fontSize: '1rem'}}>Phone</h4>
                        <p style={{margin: 0, color: 'var(--color-gray-dark)'}}>+94 77 23 53 975</p>
                      </div>
                    </div>
                    
                    <div style={{display: 'flex', alignItems: 'start', gap: 'var(--spacing-sm)'}}>
                      <div style={{minWidth: '40px', height: '40px', background: 'linear-gradient(135deg, var(--color-blue-dark), var(--color-blue-light))', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center'}}>
                        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <rect width="20" height="16" x="2" y="4" rx="2"></rect>
                          <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"></path>
                        </svg>
                      </div>
                      <div>
                        <h4 style={{marginBottom: '0.25rem', fontSize: '1rem'}}>Email</h4>
                        <p style={{margin: 0, color: 'var(--color-gray-dark)'}}>info@sevenhc.net</p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Business Hours */}
                <div className="card" style={{padding: 'var(--spacing-lg)'}}>
                  <h3 style={{marginBottom: 'var(--spacing-md)'}}>Business Hours</h3>
                  <div style={{display: 'flex', flexDirection: 'column', gap: '0.75rem'}}>
                    <div style={{display: 'flex', justifyContent: 'space-between'}}>
                      <span style={{color: 'var(--color-gray-dark)'}}>Monday - Friday</span>
                      <span style={{color: 'var(--color-white)'}}>9:00 AM - 6:00 PM</span>
                    </div>
                    <div style={{display: 'flex', justifyContent: 'space-between'}}>
                      <span style={{color: 'var(--color-gray-dark)'}}>Saturday</span>
                      <span style={{color: 'var(--color-white)'}}>10:00 AM - 4:00 PM</span>
                    </div>
                    <div style={{display: 'flex', justifyContent: 'space-between'}}>
                      <span style={{color: 'var(--color-gray-dark)'}}>Sunday</span>
                      <span style={{color: 'var(--color-white)'}}>Closed</span>
                    </div>
                  </div>
                  <p style={{marginTop: 'var(--spacing-md)', marginBottom: 0, color: 'var(--color-gray-dark)', fontSize: '0.9375rem'}}>
                    <strong style={{color: 'var(--color-blue-light)'}}>Emergency Support:</strong> Available 24/7 for existing clients
                  </p>
                </div>

                {/* Social Links */}
                <div className="card" style={{padding: 'var(--spacing-lg)'}}>
                  <h3 style={{marginBottom: 'var(--spacing-md)'}}>Follow Us</h3>
                  <div style={{display: 'flex', gap: 'var(--spacing-sm)'}}>
                    <a href="#" style={{width: '50px', height: '50px', backgroundColor: 'var(--color-black-lighter)', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center'}}>
                      <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                      </svg>
                    </a>
                    <a href="#" style={{width: '50px', height: '50px', backgroundColor: 'var(--color-black-lighter)', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center'}}>
                      <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M23.953 4.57a10 10 0 01-2.825.775 4.958 4.958 0 002.163-2.723c-.951.555-2.005.959-3.127 1.184a4.92 4.92 0 00-8.384 4.482C7.69 8.095 4.067 6.13 1.64 3.162a4.822 4.822 0 00-.666 2.475c0 1.71.87 3.213 2.188 4.096a4.904 4.904 0 01-2.228-.616v.06a4.923 4.923 0 003.946 4.827 4.996 4.996 0 01-2.212.085 4.936 4.936 0 004.604 3.417 9.867 9.867 0 01-6.102 2.105c-.39 0-.779-.023-1.17-.067a13.995 13.995 0 007.557 2.209c9.053 0 13.998-7.496 13.998-13.985 0-.21 0-.42-.015-.63A9.935 9.935 0 0024 4.59z"/>
                      </svg>
                    </a>
                    <a href="#" style={{width: '50px', height: '50px', backgroundColor: 'var(--color-black-lighter)', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center'}}>
                      <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                      </svg>
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="section" style={{backgroundColor: '#0F0F0F'}}>
        <div className="container-narrow">
          <div className="section-header">
            <h2 className="section-title">Frequently Asked Questions</h2>
          </div>

          <div style={{display: 'flex', flexDirection: 'column', gap: 'var(--spacing-md)'}}>
            <div className="card reveal-fade">
              <h4 style={{color: 'var(--color-white)', marginBottom: 'var(--spacing-sm)'}}>What is your typical project timeline?</h4>
              <p style={{margin: 0, color: 'var(--color-gray-dark)'}}>Project timelines vary based on scope and complexity. A simple website might take 4-6 weeks, while custom software could take 3-6 months. We'll provide a detailed timeline during the consultation.</p>
            </div>

            <div className="card reveal-fade delay-100">
              <h4 style={{color: 'var(--color-white)', marginBottom: 'var(--spacing-sm)'}}>Do you offer ongoing support after project completion?</h4>
              <p style={{margin: 0, color: 'var(--color-gray-dark)'}}>Yes! We provide comprehensive post-launch support including maintenance, updates, troubleshooting, and enhancements. Support packages can be customized to your needs.</p>
            </div>

            <div className="card reveal-fade delay-200">
              <h4 style={{color: 'var(--color-white)', marginBottom: 'var(--spacing-sm)'}}>What technologies do you work with?</h4>
              <p style={{margin: 0, color: 'var(--color-gray-dark)'}}>We work with modern tech stacks including React, Node.js, Python, .NET, Java, cloud platforms (AWS, Azure), databases (SQL, MongoDB), and more. We choose the best technology for each project.</p>
            </div>

            <div className="card reveal-fade delay-300">
              <h4 style={{color: 'var(--color-white)', marginBottom: 'var(--spacing-sm)'}}>Can you work with our existing systems?</h4>
              <p style={{margin: 0, color: 'var(--color-gray-dark)'}}>Absolutely! We specialize in system integration and can work with your existing infrastructure, ensuring seamless connectivity and data flow between systems.</p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ContactPage;
